import type { PipeSide, TextureKey } from '../assets/manifest.js';
import { TUNING } from '../config/tuning.js';
import { WorldScale } from './WorldScale.js';

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where and how the game runs inside the canvas. Default: the whole canvas with the game's
 * own sky, skyline, ground and HUD. A mural on a facade instead puts the game into a window of
 * the printed artwork: the background is the print, the HUD is a printed panel (see `game/src/mural`).
 */
export interface PlayfieldOptions {
  /** part of the canvas the game runs in (pixels), default: the whole canvas */
  area?: Rect;
  /**
   * Only these parts of the area show the game (pixels, relative to the area) – e.g. a window
   * with stepped edges. Everything else is cut off. WebGL only; Canvas clips to the area.
   */
  visible?: readonly Rect[];
  /**
   * What the full overlays ("Spiel beginnt", end screen) cover: polygons (pixels, relative to the area),
   * e.g. the whole window opening including the reveal faces of its stepped edges, which the game
   * itself stays behind. Default: `visible`.
   */
  cover?: readonly (readonly Point[])[];
  /** visible height in world units, default 1000. Less zooms in: figure, coins and gates appear larger. */
  worldHeight?: number;
  /** false: no own sky / skyline / ground – the background comes from outside (e.g. a printed mural) */
  scenery?: boolean;
  /** texture of the city skyline, default Cologne (see `skylineFor`) */
  skyline?: TextureKey;
  /** dark side face of the obstacle columns, default left (see `pipeSideFor`) */
  pipeSide?: PipeSide;
  /** without scenery: height of the floor above the bottom edge in world units, default 0 */
  floorHeight?: number;
  /** false: no built-in HUD – the edition shows depot, time and lives itself */
  hud?: boolean;
  /** coins stay below this line (world units from the top), default `coins.minY` */
  coinsMinY?: number;
  /** horizontal position of the figure as a fraction of the area width, default `bird.xFraction` */
  birdX?: number;
  /** where "Spiel beginnt" and the end screen put their texts (pixels, relative to the area), default the largest visible part */
  textArea?: Rect;
  /** × the usual size of those texts, default 1 */
  textScale?: number;
}

/** The area the game runs in (pixels of the canvas). */
export function playfieldArea(options: PlayfieldOptions | undefined, canvasWidth: number, canvasHeight: number): Rect {
  return options?.area ?? { x: 0, y: 0, width: canvasWidth, height: canvasHeight };
}

/** World → pixel conversion of the playfield (also used to rasterise the vector assets sharply). */
export function playfieldScale(options: PlayfieldOptions | undefined, canvasWidth: number, canvasHeight: number): WorldScale {
  const { width, height } = playfieldArea(options, canvasWidth, canvasHeight);
  return new WorldScale(width, height, options?.worldHeight);
}

/** y (pixels from the top of the area) of the surface the figure stands on. */
export function playfieldFloorY(options: PlayfieldOptions | undefined, ws: WorldScale): number {
  const floor = options?.scenery === false ? (options.floorHeight ?? 0) : TUNING.world.groundHeight;
  return ws.pixelHeight - ws.px(floor);
}
